// Firestore-backed angler catalog.
//
// Species live in `fishSpecies` and lures in `lures`. Both are public
// read-only collections (firestore.rules: signed-in read, admin write) and
// shared with the dashboard app. Seed via scripts/seed-angler-catalog.js.
//
// We fetch each collection once per session and cache in module scope, then
// filter in memory for the picker. Collections are small (~50 / ~30 docs),
// so a one-shot fetch is faster and simpler than per-keystroke queries.

import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

let speciesCache = null;
let luresCache = null;
let speciesPromise = null;
let luresPromise = null;

async function fetchSpecies() {
  if (speciesCache) return speciesCache;
  if (!speciesPromise) {
    speciesPromise = getDocs(collection(db, "fishSpecies"))
      .then((snap) => {
        const docs = snap.docs
          .map((d) => {
            const data = d.data() || {};
            const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
            const goodWeightLbs = num(data.goodWeightLbs);
            const goodLengthIn = num(data.goodLengthIn);
            return {
              id: d.id,
              label: String(data.label || data.name || d.id),
              waters: Array.isArray(data.waters) ? data.waters : [],
              order: typeof data.order === "number" ? data.order : 9999,
              // "Quality fish" thresholds. Imperial is canonical; metric is read
              // from Firestore when present, else derived so callers always have
              // both units (lbs/kg, in/cm).
              goodWeightLbs,
              goodLengthIn,
              goodWeightKg:
                num(data.goodWeightKg) ??
                (goodWeightLbs == null ? null : Math.round(goodWeightLbs * 0.45359237 * 10) / 10),
              goodLengthCm:
                num(data.goodLengthCm) ??
                (goodLengthIn == null ? null : Math.round(goodLengthIn * 2.54)),
            };
          })
          .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
        speciesCache = docs;
        return docs;
      })
      .catch((err) => {
        speciesPromise = null;
        throw err;
      });
  }
  return speciesPromise;
}

async function fetchLures() {
  if (luresCache) return luresCache;
  if (!luresPromise) {
    luresPromise = getDocs(collection(db, "lures"))
      .then((snap) => {
        const docs = snap.docs
          .map((d) => {
            const data = d.data() || {};
            return {
              id: d.id,
              label: String(data.label || data.name || d.id),
              type: String(data.type || "lure"),
              category: String(data.category || ""),
              order: typeof data.order === "number" ? data.order : 9999,
            };
          })
          .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
        luresCache = docs;
        return docs;
      })
      .catch((err) => {
        luresPromise = null;
        throw err;
      });
  }
  return luresPromise;
}

const normalize = (s) => String(s || "").trim().toLowerCase();

export function searchSpecies(species, query, waterType = "") {
  const q = normalize(query);
  const water = normalize(waterType);
  const list = Array.isArray(species) ? species : [];
  return list
    .filter((entry) => {
      const matchesWater =
        !water ||
        (Array.isArray(entry.waters) && entry.waters.includes(water));
      const matchesQuery =
        !q ||
        normalize(entry.label).includes(q) ||
        normalize(entry.id).includes(q);
      return matchesWater && matchesQuery;
    })
    .slice(0, 12);
}

export function searchLures(lures, query, typeFilter = null) {
  const q = normalize(query);
  const list = Array.isArray(lures) ? lures : [];
  const typed = typeFilter
    ? list.filter((entry) => normalize(entry.type) === normalize(typeFilter))
    : list;
  if (!q) return typed.slice(0, 12);
  return typed
    .filter(
      (entry) =>
        normalize(entry.label).includes(q) || normalize(entry.id).includes(q),
    )
    .slice(0, 12);
}

export function findById(list, id) {
  if (!id || !Array.isArray(list)) return null;
  return list.find((e) => e.id === id) || null;
}

/**
 * React hook that loads + caches the species and lure catalogs from
 * Firestore. Returns plain arrays plus a loading flag and any error.
 */
export function useAnglerCatalog() {
  const [species, setSpecies] = useState(speciesCache || []);
  const [lures, setLures] = useState(luresCache || []);
  const [loading, setLoading] = useState(!speciesCache || !luresCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchSpecies(), fetchLures()])
      .then(([sp, lu]) => {
        if (cancelled) return;
        setSpecies(sp);
        setLures(lu);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { species, lures, loading, error };
}
